import {
  DEFAULT_MODEL_BY_PROVIDER,
  type ProviderKind,
  type ServerProvider,
} from "@cmmd-forge/contracts";
import { resolveModelSlugForProvider } from "@cmmd-forge/shared/model";

import { resolveSelectableProvider } from "./providerModels";

/** The provider/model pair a project stores as its new-thread default. */
export interface ProjectDefaultModelPair {
  readonly provider: ProviderKind;
  readonly model: string;
}

export interface ProjectDefaultModelState {
  /** What the picker should show — always runnable on THIS connection. */
  readonly selection: ProjectDefaultModelPair;
  /** Whether a person chose it, which is what makes a reset meaningful. */
  readonly isExplicit: boolean;
}

/**
 * Resolve what a project's new-thread default is, seen from one connection.
 *
 * A stored pair is not automatically a usable one. The provider it names may
 * not be installed here — the same project is open on a laptop, a desktop and
 * a sprite, and they do not carry the same providers — and the slug may belong
 * to a different vendor entirely.
 *
 * Both are healed through the seams that already own those decisions rather
 * than re-derived: `resolveSelectableProvider` for presence, and
 * `resolveModelSlugForProvider` for the pair, which is the same function the
 * store heals with on read. Two approximations of one predicate is how Forge
 * shipped `{"provider":"claudeAgent","model":"gpt-5.6-sol"}` to a live runtime.
 */
export function resolveProjectDefaultModelState(
  stored: ProjectDefaultModelPair | null,
  providers: readonly ServerProvider[],
): ProjectDefaultModelState {
  if (stored === null) {
    // The same healer the stored branch uses, asked with no preference. It
    // still names a provider for a connection that published nothing, which
    // this settings row needs: it renders a picker, and a picker has to show
    // something. Nothing is dispatched from here.
    const provider = resolveSelectableProvider(providers, null);
    return {
      selection: { provider, model: DEFAULT_MODEL_BY_PROVIDER[provider] },
      isExplicit: false,
    };
  }

  const provider = resolveSelectableProvider(providers, stored.provider);
  // A fallback changed the provider, so the stored model belongs to something
  // that is not running here. Carrying it across is precisely the mispairing
  // the resolver below exists to refuse, arrived at from the other direction.
  const model =
    provider === stored.provider
      ? resolveModelSlugForProvider(provider, stored.model)
      : DEFAULT_MODEL_BY_PROVIDER[provider];

  return { selection: { provider, model }, isExplicit: true };
}
